import { forwardRef } from 'react'

import { clsx } from 'clsx'

import { ClearButton, ChevronIcon, SelectedContent } from './multi-select/chips'
import { DropdownPanel, DropdownPortal } from './multi-select/dropdown'
import { defaultFilterOption, useMultiSelectSetup } from './multi-select/hooks'
import { sizeStyles, validationStyles } from './multi-select/types'
import { useIsMobile } from './popover'

export type { MultiSelectProps } from './multi-select/types'
import type { MultiSelectResolvedProps, MultiSelectSetupResult } from './multi-select/hooks'
import type { MultiSelectProps } from './multi-select/types'
import type { SelectOption } from './searchable-select'

// ============================================================================
// Trigger button
// ============================================================================

interface TriggerButtonProps<T> {
  buttonRef: React.RefObject<HTMLButtonElement>
  isOpen: boolean
  resolved: MultiSelectResolvedProps<T>
  selectedOptions: SelectOption<T>[]
  validationStyle: string
  onClear: (e: React.MouseEvent | React.KeyboardEvent) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onRemoveChip: (v: T) => void
  onToggleOpen: () => void
}

function TriggerButton<T>({
  buttonRef, isOpen, resolved, selectedOptions, validationStyle,
  onClear, onKeyDown, onRemoveChip, onToggleOpen,
}: TriggerButtonProps<T>): JSX.Element {
  const { className, id, isClearable, isDisabled, isShowCount, placeholder, renderChip, size, value } = resolved
  const sizeClasses = sizeStyles[size]
  return (
    <button
      ref={buttonRef}
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      className={clsx(
        'w-full rounded-md border bg-card text-foreground',
        'focus:ring-1 focus:outline-none',
        'disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150',
        'flex flex-wrap items-center gap-1',
        sizeClasses.padding, sizeClasses.minHeight, validationStyle, className
      )}
      disabled={isDisabled}
      id={id}
      type="button"
      onClick={onToggleOpen}
      onKeyDown={onKeyDown}
    >
      <div className="flex flex-wrap gap-1 flex-1 overflow-hidden">
        <SelectedContent
          placeholder={placeholder}
          renderChip={renderChip}
          selectedOptions={selectedOptions}
          showCount={isShowCount}
          textClass={sizeClasses.text}
          onRemoveChip={onRemoveChip}
        />
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {isClearable && value.length > 0 && !isDisabled && <ClearButton onClear={onClear} />}
        <ChevronIcon isOpen={isOpen} />
      </div>
    </button>
  )
}

// ============================================================================
// Message area
// ============================================================================

interface MessageAreaProps {
  error?: string
  helperText?: string
  isHideMessage: boolean
  showError: boolean | '' | undefined
}

function MessageArea({ error, helperText, isHideMessage, showError }: MessageAreaProps): JSX.Element | null {
  if (isHideMessage) { return null }
  return (
    <div className="field-message">
      {showError && <p className="text-sm text-red-500" role="alert">{error}</p>}
      {!showError && helperText && <p className="text-sm text-muted-foreground">{helperText}</p>}
    </div>
  )
}

// ============================================================================
// Open dropdown portal
// ============================================================================

interface OpenDropdownProps<T> {
  dropdownRef: React.RefObject<HTMLDivElement>
  filteredOptions: SelectOption<T>[]
  highlightedIndex: number
  isMobile: boolean
  listRef: React.RefObject<HTMLDivElement>
  resolved: MultiSelectResolvedProps<T>
  searchInputRef: React.RefObject<HTMLInputElement>
  searchQuery: string
  setup: Pick<MultiSelectSetupResult<T>, 'dropdownPosition' | 'handleClose' | 'handleToggleOption' | 'setSearchQuery'>
}

function OpenDropdown<T>({
  dropdownRef, filteredOptions, highlightedIndex, isMobile, listRef, resolved,
  searchInputRef, searchQuery, setup,
}: OpenDropdownProps<T>): JSX.Element {
  const { dropdownPosition, handleClose, handleToggleOption, setSearchQuery } = setup
  return (
    <DropdownPortal dropdownRef={dropdownRef} isMobile={isMobile} isModal={resolved.isModal} onClose={handleClose}>
      <DropdownPanel
        dropdownPosition={dropdownPosition}
        filteredOptions={filteredOptions}
        highlightedIndex={highlightedIndex}
        isLoading={resolved.isLoading}
        isMobile={isMobile}
        isModal={resolved.isModal}
        listRef={listRef}
        maxSelections={resolved.maxSelections}
        maxVisibleItems={resolved.maxVisibleItems}
        noOptionsText={resolved.noOptionsText}
        renderOption={resolved.renderOption}
        searchInputRef={searchInputRef}
        searchPlaceholder={resolved.searchPlaceholder}
        searchQuery={searchQuery}
        value={resolved.value}
        onSearchChange={setSearchQuery}
        onToggle={handleToggleOption}
      />
    </DropdownPortal>
  )
}

// ============================================================================
// MultiSelect view — renders the full UI from already-computed setup
// ============================================================================

interface MultiSelectViewProps<T> {
  isMobile: boolean
  isOpen: boolean
  setup: MultiSelectSetupResult<T>
  showError: boolean | '' | undefined
  validationState: string
}

function MultiSelectView<T>({ isMobile, isOpen, setup, showError, validationState }: MultiSelectViewProps<T>): JSX.Element {
  const { filteredOptions, handleClear, handleKeyDown, handleRemoveChip, highlightedIndex,
    refs: { buttonRef, containerRef, dropdownRef, listRef, searchInputRef },
    resolved, selectedOptions, setIsOpen, searchQuery } = setup
  return (
    <div ref={containerRef} className="w-full">
      <TriggerButton
        buttonRef={buttonRef}
        isOpen={isOpen}
        resolved={resolved}
        selectedOptions={selectedOptions}
        validationStyle={validationStyles[validationState as keyof typeof validationStyles]}
        onClear={handleClear}
        onKeyDown={handleKeyDown}
        onRemoveChip={handleRemoveChip}
        onToggleOpen={() => { if (!resolved.isDisabled) { setIsOpen(!isOpen) } }}
      />
      {isOpen && (
        <OpenDropdown
          dropdownRef={dropdownRef}
          filteredOptions={filteredOptions}
          highlightedIndex={highlightedIndex}
          isMobile={isMobile}
          listRef={listRef}
          resolved={resolved}
          searchInputRef={searchInputRef}
          searchQuery={searchQuery}
          setup={setup}
        />
      )}
      <MessageArea
        error={resolved.error}
        helperText={resolved.helperText}
        isHideMessage={resolved.isHideMessage}
        showError={showError}
      />
    </div>
  )
}

// ============================================================================
// Main component
// ============================================================================

/**
 * MultiSelect component for selecting multiple options with chips/tags display
 *
 * Features:
 * - Searchable dropdown with checkboxes
 * - Selected items shown as removable chips
 * - Keyboard navigation
 * - Desktop dropdown / Mobile bottom sheet
 * - Maximum selection limit
 *
 * @example
 * // Basic usage
 * <MultiSelect
 *   options={accountOptions}
 *   value={selectedAccounts}
 *   onChange={setSelectedAccounts}
 *   placeholder="Select accounts..."
 * />
 */
function MultiSelectInner<T = string>(
  props: MultiSelectProps<T>,
  ref: React.ForwardedRef<HTMLButtonElement>
): JSX.Element {
  const isMobile = useIsMobile()
  const setup = useMultiSelectSetup(props, isMobile, ref)
  const { isOpen, showError, validationState } = setup
  return <MultiSelectView isMobile={isMobile} isOpen={isOpen} setup={setup} showError={showError} validationState={validationState} />
}

export const MultiSelect = forwardRef(MultiSelectInner) as <T = string>(
  props: MultiSelectProps<T> & { ref?: React.ForwardedRef<HTMLButtonElement> }
) => ReturnType<typeof MultiSelectInner>

export { defaultFilterOption }

export default MultiSelect
